/**
 * stuburt-gift-cap.js — <stuburt-gift-cap>
 *
 * Gift-with-purchase automation for the Aug-26 Lit clearance: once the bag
 * holds £35 or more of POLOS, a Devon Cap is added for the customer, and taken
 * back out again if the polo total drops back below.
 *
 * ⚠️ The threshold here is NOT the source of truth — settings.stuburt_capgift_threshold
 * is, and it MUST equal the minimum purchase amount on the automatic 'Buy X get Y'
 * discount in admin. When the theme's number is the higher of the two you get the
 * worst possible failure: the customer can add a cap by hand and it rings up at £0
 * (the discount is satisfied) while the automation stays silent (its threshold is
 * not), which reads as "the free cap is broken". That is exactly what £40-here /
 * £35-in-admin did to two Alicante polos at £39.98 — see 2026-08-23.
 *
 * Third sibling of assets/stuburt-gift-keychain.js (Play Well — counts ITEMS
 * from a collection) and docs/fireballs-gift/stuburt-gift-bib.js (Fireballs —
 * counts MONEY across the whole bag). This one counts MONEY FROM A COLLECTION,
 * which is the combination neither of the other two does:
 *
 *     keychain   items  ×  collection
 *     bib        money  ×  whole bag
 *     cap        money  ×  collection     ← here
 *
 * Shopify cannot do this on its own. An automatic "Buy X get Y" discount can
 * make the cap free, but it never PUTS it in the cart — the customer has to add
 * it. So the two halves are:
 *
 *   1. the discount (admin)  — makes the line £0
 *   2. this element (theme)  — puts the line there, and takes it away again
 *
 * Because half the mechanic lives in the admin, the element refuses to leave a
 * chargeable line behind: after every add it re-reads the cart, and if the cap
 * is NOT fully discounted it removes it again and switches itself off for the
 * session. A missing, expired or non-combining discount degrades to "no cap",
 * never to "charged £13.99 for something they didn't pick".
 *
 * ⚠️ TWO GIFT OFFERS AT ONCE: two automatic Buy X get Y discounts DO stack,
 * but only if BOTH have Combinations → "Product discounts" enabled. If either
 * one does not, a customer earning both gifts gets one free line and one
 * chargeable one — and the losing component stands itself down for the session
 * via the £0 check below. That is the designed failure, not a bug, but it means
 * the two discounts' combination settings have to be checked together, not
 * assumed.
 *
 * Markup + config: snippets/stuburt-gift-cap.liquid (mounted once in
 * layout/theme.liquid, so it works on every page, not just the cart).
 *
 * The threshold is measured on final_line_price of qualifying lines only — what
 * the customer is actually charged for the polos, after sale prices and any
 * automatic line discounts, before shipping and tax. The gift line itself is
 * always excluded, so the offer can never pay for itself.
 *
 * Custom Element; all listeners bound to an AbortController and torn down in
 * disconnectedCallback.
 */

/** Session keys. Per-tab-session on purpose: a removal should be respected for
 *  the visit, not for ever. Namespaced away from the keychain's and the bib's
 *  keys so the offers never read each other's state. */
const KEY_ADDED = 'stuburt:capgift:added';
const KEY_DISMISSED = 'stuburt:capgift:dismissed';
const KEY_BLOCKED = 'stuburt:capgift:blocked';

/** Line property that marks a cap WE added, so a cap the customer chose
 *  themselves is never silently removed. Leading underscore keeps it out of the
 *  cart line's visible properties (see snippets/cart-products.liquid). */
const GIFT_PROPERTY = '_stuburt_cap_gift';

/** Our own cart:update source, so the reconcile loop ignores its own echo. */
const SOURCE = 'stuburt-gift-cap';

class StuburtGiftCap extends HTMLElement {
  #abort = null;
  #config = null;
  #busy = false;
  #pending = false;
  #timer = 0;

  connectedCallback() {
    const variantId = this.dataset.giftVariant;
    const giftProductId = this.dataset.giftProduct;
    const threshold = parseInt(this.dataset.threshold, 10); // pence

    // Product ids of the qualifying collection, rendered by the snippet. Only
    // these lines count towards the threshold.
    const qualifying = new Set(
      (this.dataset.qualifying || '').split(',').map((id) => id.trim()).filter(Boolean)
    );

    // The snippet only renders when all of this is present, but a half-set-up
    // gift (draft product, sold-out cap, empty collection) must be inert rather
    // than noisy.
    if (!variantId || !giftProductId || !threshold || qualifying.size === 0) return;

    // Liquid caps a collection loop at 50 products. If the qualifying collection
    // has grown past that, the offer would quietly stop counting the overflow —
    // say so in the console rather than fail silently.
    const declared = parseInt(this.dataset.qualifyingCount, 10);
    if (Number.isFinite(declared) && declared > qualifying.size) {
      console.warn(
        `[stuburt-gift-cap] qualifying collection holds ${declared} products but only ` +
        `${qualifying.size} were rendered — products beyond the first 50 will not count ` +
        `towards the threshold. Narrow the collection or paginate the snippet.`
      );
    }

    this.#config = { variantId, giftProductId, threshold, qualifying };

    this.#abort = new AbortController();
    document.addEventListener('cart:update', this.#onCartUpdate, { signal: this.#abort.signal });

    // Reconcile once on load too: the qualifying add may have happened on a
    // previous page view, or the cart may have been changed in another tab.
    this.#schedule();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    clearTimeout(this.#timer);
    this.#busy = false;
    this.#pending = false;
  }

  #onCartUpdate = (event) => {
    if (event.detail?.data?.source === SOURCE) return;
    this.#schedule();
  };

  /** Coalesce the burst of events a single cart change can fire. */
  #schedule() {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#reconcile(), 150);
  }

  async #reconcile() {
    if (this.#read(KEY_BLOCKED)) return;
    // A change that lands mid-flight must not be dropped, or the cart can settle
    // out of step with the offer. Queue one re-run instead.
    if (this.#busy) {
      this.#pending = true;
      return;
    }
    this.#busy = true;

    try {
      const cart = await this.#cart();
      if (!cart) return;

      const { giftProductId, threshold, qualifying } = this.#config;
      let qualifyingTotal = 0;
      let giftLine = null;
      let chosenLine = null;

      for (const item of cart.items || []) {
        const productId = String(item.product_id);
        const isGift = productId === giftProductId && item.properties?.[GIFT_PROPERTY];
        if (isGift) {
          giftLine = item;
          continue; // never let the gift help pay for itself
        }
        if (productId === giftProductId) chosenLine = item;
        // The only difference from the bib: whole-bag money becomes
        // collection-scoped money. A cap in the bag never counts towards
        // earning a cap, and neither does anything else outside the collection.
        if (qualifying.has(productId)) qualifyingTotal += item.final_line_price;
      }

      if (qualifyingTotal >= threshold) {
        // Already there — just make sure a nudged quantity can't ride along,
        // since the discount only frees one.
        if (giftLine) {
          if (giftLine.quantity > 1) await this.#change(giftLine.key, 1);
          return;
        }
        // They put a cap in the bag themselves; the discount covers it, and
        // giving them a second one is not the offer.
        if (chosenLine) return;
        if (this.#read(KEY_DISMISSED)) return;
        // We added it and it is gone while they still qualify — they took it
        // out. Respect that for the rest of the visit instead of fighting them.
        if (this.#read(KEY_ADDED)) {
          this.#write(KEY_DISMISSED, '1');
          this.#clear(KEY_ADDED);
          return;
        }
        await this.#add();
      } else {
        // Below the threshold. CUSTOM (Stuburt, 2026-08-23): clear the refusal here.
        //
        // KEY_DISMISSED used to be written once, above, and cleared NOWHERE — so a
        // customer who took the cap out was refused for the whole session even after
        // the basket that the refusal was made against had gone. Repro that found it:
        // remove the cap (dismissed), remove the polos (drops below — this branch used
        // to run only `if (giftLine)`, and the gift line was already gone, so nothing
        // happened), then add the polos back. They qualify again and no cap arrives.
        //
        // Clearing it on the way down is what makes the refusal bind to the basket it
        // was made in, which is what this offer always claimed to do. Re-qualifying is
        // a fresh decision and gets a fresh cap; taking it out again re-refuses, and
        // that costs one tap rather than stranding the offer for the visit.
        this.#clear(KEY_DISMISSED);
        this.#clear(KEY_ADDED);
        if (giftLine) await this.#change(giftLine.key, 0);
      }
    } catch {
      // A failed gift add is never worth breaking the cart over — the next
      // cart:update will try again.
    } finally {
      this.#busy = false;
      if (this.#pending) {
        this.#pending = false;
        this.#schedule();
      }
    }
  }

  async #cart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) return null;
    return res.json();
  }

  async #add() {
    const body = new FormData();
    body.set('id', this.#config.variantId);
    body.set('quantity', '1');
    body.set(`properties[${GIFT_PROPERTY}]`, 'true');

    // No X-Requested-With: the store's bot protection answers cart adds carrying
    // AJAX markers with a fake 422 "sold out" (see error ledger 2026-07-05).
    const res = await fetch('/cart/add.js', { method: 'POST', body, headers: { Accept: 'application/json' } });
    if (!res.ok) return;

    this.#write(KEY_ADDED, '1');

    // The safety net. Automatic discounts apply to the cart, not just checkout,
    // so a correctly configured "Buy X get Y" leaves this line at zero. Anything
    // else means the discount is missing, expired, or losing to another
    // automatic product discount (the Play Well keychain offer is the live
    // candidate) — back the line out and stand down rather than charge for an
    // unrequested item.
    const cart = await this.#cart();
    const line = cart?.items?.find((item) => item.properties?.[GIFT_PROPERTY]);

    if (line && line.final_line_price > 0) {
      this.#write(KEY_BLOCKED, '1');
      this.#clear(KEY_ADDED);
      await this.#change(line.key, 0);
      console.warn('[stuburt-gift-cap] the cap was not free in the cart — gift automation stood down for this session');
      return;
    }

    this.#notify(cart);
  }

  async #change(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity }),
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    });
    if (!res.ok) return;
    this.#notify(await res.json());
  }

  /** Tell the cart icon, drawer and cart page to re-render server-side. */
  #notify(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          data: {
            // Not 'product-form-component': that would make the bubble ADD to
            // its count and pop the mini-cart. An absolute count is what we want.
            source: SOURCE,
            itemCount: cart?.item_count ?? 0,
            quiet: true,
          },
        },
      })
    );
  }

  #read(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  #write(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* private mode — the gift just won't be sticky */
    }
  }

  #clear(key) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* no-op */
    }
  }
}

if (!customElements.get('stuburt-gift-cap')) {
  customElements.define('stuburt-gift-cap', StuburtGiftCap);
}
